import React, { useState } from 'react';
import { Container, Grid, Typography, Box, Chip, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';
import filmhookLogo from '../../../assets/Filmhook_Apps_Logoonly.png';

// Icons for capabilities
import VerifiedIcon from '@mui/icons-material/Verified';
import MovieIcon from '@mui/icons-material/Movie';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import SmartToyIcon from '@mui/icons-material/SmartToy';

// App Stores icons & cinema badges
import ShopIcon from '@mui/icons-material/Shop';
import AppleIcon from '@mui/icons-material/Apple';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

const capabilities = [
  { icon: <VerifiedIcon />, title: 'Verified Profiles', desc: 'Gold & Silver badges' },
  { icon: <MovieIcon />, title: 'Audition Listings', desc: 'Verified opportunities' },
  { icon: <StorefrontIcon />, title: 'Marketplace', desc: 'Connect & collaborate' },
  { icon: <LocationOnIcon />, title: 'Shooting Locations', desc: 'Find perfect spots' },
  { icon: <LiveTvIcon />, title: 'Live Streaming', desc: 'Real-time engagement' },
  { icon: <SmartToyIcon />, title: 'AI Chatbot', desc: 'Ask Nemo' },
];

const CompanyOverview = () => {
  const { isDarkMode } = useTheme();

  // Animation trigger key for falling F-Logo
  const [dropCount, setDropCount] = useState(0);
  const [isImpacted, setIsImpacted] = useState(false);

  const handleTriggerFall = () => {
    setIsImpacted(false);
    setDropCount((prev) => prev + 1);
    setTimeout(() => {
      setIsImpacted(true);
    }, 600);
  };

  const containerBg = isDarkMode ? 'linear-gradient(135deg, #0a0e1a 0%, #131b2f 100%)' : 'linear-gradient(135deg, #f0f7ff 0%, #e6f0fa 100%)';
  const cardBg = isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
  const textPrimary = isDarkMode ? '#ffffff' : '#0d1530';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';

  return (
    <Box sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 4, md: 6 }, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Box
          sx={{
            background: containerBg,
            borderRadius: '32px',
            overflow: 'hidden',
            border: `1px solid ${borderColor}`,
            boxShadow: isDarkMode ? '0 24px 48px rgba(0,0,0,0.3)' : '0 24px 48px rgba(7, 164, 245,0.08)',
            p: { xs: 3, md: 6, lg: 8 },
            position: 'relative',
          }}
        >
          <Grid container spacing={6} alignItems="center">
            
            {/* Left Content */}
            <Grid item xs={12} lg={7}>
              <motion.div initial={{ x: -40, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                
                <Chip
                  label="FOUNDED SEP 2023 · BENGALURU, INDIA"
                  size="small"
                  sx={{ background: isDarkMode ? 'rgba(7, 164, 245,0.2)' : '#e0f2fe', color: '#0ea5e9', fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.05em', mb: 3 }}
                />
                
                <Typography variant="h3" sx={{ fontWeight: 900, color: textPrimary, fontSize: { xs: '2.2rem', md: '3.2rem' }, mb: 3, lineHeight: 1.2 }}>
                  About Film-hook <Box component="span" sx={{ color: '#07a4f5' }}>Mediaapps</Box>
                </Typography>
                
                <Typography variant="body1" sx={{ color: textSecondary, lineHeight: 1.8, fontSize: '1.05rem', mb: 5, maxWidth: '90%' }}>
                  <strong style={{ color: textPrimary }}>Film-hook Mediaapps Private Limited</strong>, founded in September 2023 and headquartered in Bengaluru, Karnataka, is a forward-thinking company focused on delivering digital transformation solutions specifically for the film and entertainment industry. Its flagship platform, <strong style={{ color: '#07a4f5' }}>Filmhook</strong>, is a dynamic social network designed to connect filmmakers, actors, producers, directors, and creative professionals worldwide.
                </Typography>

                {/* Capabilities Grid */}
                <Grid container spacing={2} sx={{ mb: 5 }}>
                  {capabilities.map((cap, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: cardBg, border: `1px solid ${borderColor}`, p: 1.5, borderRadius: '12px' }}>
                        <Box sx={{ color: '#07a4f5', display: 'flex' }}>{cap.icon}</Box>
                        <Box>
                          <Typography sx={{ color: textPrimary, fontSize: '0.8rem', fontWeight: 800 }}>{cap.title}</Typography>
                          <Typography sx={{ color: textSecondary, fontSize: '0.7rem' }}>{cap.desc}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ height: '1px', background: borderColor, mb: 4 }} />

                {/* Footer Info */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 4 } }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: textSecondary, fontSize: '0.9rem' }}>Available on the</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>
                      <ShopIcon sx={{ color: '#10b981' }} /> Google Play Store
                    </Box>
                    <Typography sx={{ color: textSecondary, fontSize: '0.9rem', display: { xs: 'none', sm: 'block' } }}>|</Typography>
                    <Typography sx={{ color: textSecondary, fontSize: '0.9rem' }}>Coming soon on</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>
                      <AppleIcon sx={{ color: '#0ea5e9' }} /> App Store
                    </Box>
                  </Box>
                </Box>
                
                {/* Contact Pills */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: cardBg, border: `1px solid ${borderColor}`, px: 2, py: 1, borderRadius: '24px' }}>
                    <EmailIcon sx={{ color: '#07a4f5', fontSize: '1rem' }} />
                    <Typography sx={{ color: textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>support@filmhookapps.com</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: cardBg, border: `1px solid ${borderColor}`, px: 2, py: 1, borderRadius: '24px' }}>
                    <PhoneIcon sx={{ color: '#e11d48', fontSize: '1rem' }} />
                    <Typography sx={{ color: textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>+91 9629757656</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: cardBg, border: `1px solid ${borderColor}`, px: 2, py: 1, borderRadius: '24px' }}>
                    <LanguageIcon sx={{ color: '#0ea5e9', fontSize: '1rem' }} />
                    <Typography sx={{ color: textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>filmhookapps.com</Typography>
                  </Box>
                </Box>
                
              </motion.div>
            </Grid>

            {/* Right: Interactive 3D Cinema F-Logo Falling & Holographic Showcase */}
            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  borderRadius: '30px',
                  background: isDarkMode
                    ? 'radial-gradient(circle at 50% 40%, rgba(7, 164, 245, 0.16) 0%, rgba(10, 16, 32, 0.95) 75%)'
                    : 'radial-gradient(circle at 50% 40%, rgba(7, 164, 245, 0.18) 0%, #ffffff 80%)',
                  border: isDarkMode ? '1.5px solid rgba(7, 164, 245, 0.28)' : '1.5px solid rgba(7, 164, 245, 0.2)',
                  boxShadow: isDarkMode
                    ? '0 30px 80px rgba(0, 0, 0, 0.8), 0 0 60px rgba(7, 164, 245, 0.2)'
                    : '0 25px 60px rgba(7, 164, 245, 0.14), 0 4px 20px rgba(0, 0, 0, 0.04)',
                  p: { xs: 3, sm: 4 },
                  overflow: 'hidden',
                  cursor: 'pointer',
                  minHeight: { xs: 440, sm: 500 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                onClick={handleTriggerFall}
              >
                {/* Cinema HUD Top Bar */}
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1.5,
                    borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                    zIndex: 4,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: '#ef4444',
                        boxShadow: '0 0 8px #ef4444',
                        animation: 'pulse 1.2s infinite',
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                      }}
                    >
                      REC ● 00:09:23:24 • CINEMA CORE
                    </Typography>
                  </Box>

                  <Chip
                    icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '13px !important', color: '#07a4f5' }} />}
                    label="Official Emblem"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: 'rgba(7, 164, 245, 0.12)',
                      color: '#07a4f5',
                      border: '1px solid rgba(7, 164, 245, 0.3)',
                    }}
                  />
                </Box>

                {/* Central Cinema Stage: 3D Holographic Pedestal & Falling F-Logo */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 320,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    my: 1,
                  }}
                >
                  {/* Rotating Cinema Compass Aperture */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 260,
                      height: 260,
                      borderRadius: '50%',
                      border: '1.5px dashed rgba(7, 164, 245, 0.25)',
                      animation: 'spinRing 35s linear infinite',
                      pointerEvents: 'none',
                      '@keyframes spinRing': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />

                  {/* Volumetric Radial Light Halo */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 200,
                      height: 200,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(7, 164, 245, 0.35) 0%, rgba(56, 189, 248, 0.08) 60%, transparent 100%)',
                      filter: 'blur(20px)',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Shockwave Rings on Impact */}
                  <AnimatePresence>
                    {isImpacted && (
                      <>
                        <Box
                          component={motion.div}
                          initial={{ scale: 0.2, opacity: 0.9 }}
                          animate={{ scale: 2.2, opacity: 0 }}
                          transition={{ duration: 0.85, ease: 'easeOut' }}
                          sx={{
                            position: 'absolute',
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            border: '2px solid #07a4f5',
                            boxShadow: '0 0 25px #07a4f5',
                            pointerEvents: 'none',
                          }}
                        />
                        <Box
                          component={motion.div}
                          initial={{ scale: 0.2, opacity: 0.8 }}
                          animate={{ scale: 2.6, opacity: 0 }}
                          transition={{ delay: 0.1, duration: 0.95, ease: 'easeOut' }}
                          sx={{
                            position: 'absolute',
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            border: '1.5px solid #38bdf8',
                            pointerEvents: 'none',
                          }}
                        />
                      </>
                    )}
                  </AnimatePresence>

                  {/* 3D Falling Filmhook F-Logo */}
                  <Box
                    component={motion.div}
                    key={dropCount}
                    initial={{
                      y: -280,
                      opacity: 0,
                      scale: 0.5,
                      rotateZ: -25,
                      rotateX: 45,
                    }}
                    animate={{
                      y: [ -280, 10, -18, 5, 0 ],
                      opacity: [ 0, 1, 1, 1, 1 ],
                      scale: [ 0.5, 1.18, 0.94, 1.04, 1 ],
                      rotateZ: [ -25, 6, -3, 1, 0 ],
                      rotateX: [ 45, 0, 0, 0, 0 ],
                    }}
                    transition={{
                      duration: 1.15,
                      times: [ 0, 0.6, 0.78, 0.9, 1 ],
                      ease: [ 0.22, 1, 0.36, 1 ],
                    }}
                    whileHover={{
                      scale: 1.1,
                      rotateZ: 4,
                      transition: { duration: 0.3 },
                    }}
                    sx={{
                      position: 'relative',
                      zIndex: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      component="img"
                      src={filmhookLogo}
                      alt="Filmhook F Logo"
                      sx={{
                        width: { xs: 125, sm: 145 },
                        height: 'auto',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 18px 35px rgba(7, 164, 245, 0.55)) drop-shadow(0 0 20px rgba(56, 189, 248, 0.4))',
                        animation: 'ambientFloat 3.6s ease-in-out infinite alternate',
                        '@keyframes ambientFloat': {
                          '0%': { transform: 'translateY(0px)' },
                          '100%': { transform: 'translateY(-10px)' },
                        },
                      }}
                    />

                    {/* Ground Mirror Reflection */}
                    <Box
                      component="img"
                      src={filmhookLogo}
                      alt="Reflection"
                      sx={{
                        width: { xs: 125, sm: 145 },
                        height: 'auto',
                        objectFit: 'contain',
                        transform: 'scaleY(-0.7) translateY(-14px)',
                        opacity: isDarkMode ? 0.22 : 0.15,
                        filter: 'blur(3px)',
                        maskImage: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                        pointerEvents: 'none',
                      }}
                    />
                  </Box>

                  {/* 4 Floating Cinema Telemetry Badges Around the Logo */}
                  {/* 1. Top Left: Latency */}
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                    sx={{
                      position: 'absolute',
                      top: 15,
                      left: 10,
                      background: isDarkMode ? 'rgba(10, 16, 32, 0.88)' : 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(7, 164, 245, 0.3)',
                      borderRadius: '12px',
                      px: 1.4,
                      py: 0.7,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      zIndex: 4,
                    }}
                  >
                    <BoltRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: textPrimary, lineHeight: 1.1 }}>
                        &lt; 18ms Latency
                      </Typography>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#f59e0b', lineHeight: 1.1 }}>
                        Global Edge
                      </Typography>
                    </Box>
                  </Box>

                  {/* 2. Top Right: Bengaluru HQ */}
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                    sx={{
                      position: 'absolute',
                      top: 15,
                      right: 10,
                      background: isDarkMode ? 'rgba(10, 16, 32, 0.88)' : 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '12px',
                      px: 1.4,
                      py: 0.7,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      zIndex: 4,
                    }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', boxShadow: '0 0 6px #10b981' }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: textPrimary, lineHeight: 1.1 }}>
                        Bengaluru, India
                      </Typography>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#10b981', lineHeight: 1.1 }}>
                        Founded Sep 2023
                      </Typography>
                    </Box>
                  </Box>

                  {/* 3. Bottom Left: Verified Network */}
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.5 }}
                    sx={{
                      position: 'absolute',
                      bottom: 25,
                      left: 10,
                      background: isDarkMode ? 'rgba(10, 16, 32, 0.88)' : 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(7, 164, 245, 0.3)',
                      borderRadius: '12px',
                      px: 1.4,
                      py: 0.7,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      zIndex: 4,
                    }}
                  >
                    <VerifiedRoundedIcon sx={{ fontSize: 16, color: '#07a4f5' }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: textPrimary, lineHeight: 1.1 }}>
                        Verified Talent ID
                      </Typography>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#07a4f5', lineHeight: 1.1 }}>
                        100K+ Creators
                      </Typography>
                    </Box>
                  </Box>

                  {/* 4. Bottom Right: Nemo AI */}
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.5 }}
                    sx={{
                      position: 'absolute',
                      bottom: 25,
                      right: 10,
                      background: isDarkMode ? 'rgba(10, 16, 32, 0.88)' : 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(236, 72, 153, 0.3)',
                      borderRadius: '12px',
                      px: 1.4,
                      py: 0.7,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      zIndex: 4,
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 16, color: '#ec4899' }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: textPrimary, lineHeight: 1.1 }}>
                        Ask Nemo AI
                      </Typography>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#ec4899', lineHeight: 1.1 }}>
                        Creative Assistant
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Bottom Interactive Trigger Bar */}
                <Box
                  sx={{
                    width: '100%',
                    pt: 1.5,
                    borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    zIndex: 4,
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<ReplayRoundedIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTriggerFall();
                    }}
                    sx={{
                      color: '#07a4f5',
                      bgcolor: 'rgba(7, 164, 245, 0.1)',
                      border: '1px solid rgba(7, 164, 245, 0.3)',
                      borderRadius: '20px',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      textTransform: 'none',
                      px: 1.8,
                      py: 0.5,
                      '&:hover': {
                        bgcolor: 'rgba(7, 164, 245, 0.2)',
                        transform: 'scale(1.04)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Click to Drop F-Logo
                  </Button>

                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                    }}
                  >
                    Interactive Cinema Stage
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default CompanyOverview;
